import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { CheckCircleOutline, HighlightOff, ArrowRightAlt } from '@mui/icons-material';
import { t } from '../i18n';

// "How the data is stored" — each ROW is one stored address (a postal code),
// each CELL is that address's value for one level, shown in Japanese (top) and
// English (bottom). Reverse search returns a postal code when the (normalized)
// query equals one cell exactly. Real data from the live API.
const LEVEL_HEADERS = [
  t('Prefecture / 都道府県', '都道府県 / Prefecture'),
  t('City / 市・郡', '市・郡 / City'),
  t('Ward / 区・町・村', '区・町・村 / Ward'),
  t('Town / 町域', '町域 / Town'),
  t('Building·floor / 建物・階', '建物・階 / Building·floor'),
];

const C = (ja, en) => ({ ja, en }); // a filled cell
const ADDR_ROWS = [
  { postal: '〒193-0931', cells: [C('東京都', 'Tokyo'), C('八王子市', 'Hachioji-Shi'), null, C('台町', 'Daimachi'), null] },
  { postal: '〒048-1511', cells: [C('北海道', 'Hokkaido'), C('虻田郡', 'Abuta-Gun'), C('ニセコ町', 'Niseko-Cho'), C('ニセコ', 'Niseko'), null] },
  { postal: '〒048-1501', cells: [C('北海道', 'Hokkaido'), C('虻田郡', 'Abuta-Gun'), C('ニセコ町', 'Niseko-Cho'), C('富士見', 'Fujimi'), null] },
  { postal: '〒163-1390', cells: [C('東京都', 'Tokyo'), null, C('新宿区', 'Shinjuku-Ku'), C('西新宿', 'Nishishinjuku'), C('新宿アイランドタワー', 'Shinjuku I-Land Tower')] },
  { postal: '〒163-1310', cells: [C('東京都', 'Tokyo'), null, C('新宿区', 'Shinjuku-Ku'), C('西新宿', 'Nishishinjuku'), C('新宿アイランドタワー10階', 'Shinjuku I-Land Tower 10F')] },
];

// Worked examples. Query shown bilingually; reason points at the cell it matches.
const MATCHES = [
  ['ニセコ / Niseko', t('Town cell → 〒048-1511 only (not 富士見 / Fujimi)', '町域セル → 〒048-1511 のみ（富士見は対象外）')],
  ['ニセコ町 / Niseko-Cho', t('Ward cell → all of Niseko-Cho: 〒048-1511, 〒048-1501 (Fujimi), …', '区・町・村セル → ニセコ町すべて：〒048-1511・〒048-1501（富士見）など')],
  ['新宿アイランドタワー / Shinjuku I-Land Tower', t('Building cell → 〒163-1390 (whole building)', '建物セル → 〒163-1390（建物全体）')],
  ['新宿アイランドタワー10階 / Shinjuku I-Land Tower 10F', t('Building cell → 〒163-1310 (10th floor)', '建物セル → 〒163-1310（10階）')],
];
const FAILS = [
  ['アイランドタワー / I-Land Tower', t('only part of a cell — type the whole value: 新宿アイランドタワー or …10階', 'セルの一部だけ — 値全体を入力：「新宿アイランドタワー」または「…10階」')],
  ['新宿アイランドタワー / Shinjuku I-Land Tower', t("won't reach the 10th floor (163-1310) — add 10階", '10階（163-1310）には届きません —「10階」を付ける')],
  ['東京都八王子市 / Tokyo Hachioji-Shi', t('two cells joined (Prefecture + City)', '2つのセルを連結（都道府県＋市）')],
];

const DataCell = ({ cell }) =>
  cell ? (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{cell.ja}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{cell.en}</Typography>
    </Box>
  ) : (
    <Typography component="span" color="text.disabled">—</Typography>
  );

const ExampleRow = ({ ok, query, reason }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, flexWrap: 'wrap' }}>
    {ok ? (
      <CheckCircleOutline fontSize="small" color="success" />
    ) : (
      <HighlightOff fontSize="small" color="error" />
    )}
    <Typography component="span" sx={{ fontWeight: 600 }}>{query}</Typography>
    <ArrowRightAlt fontSize="small" sx={{ color: 'text.disabled' }} />
    <Typography component="span" variant="body2" color="text.secondary">{reason}</Typography>
  </Box>
);

const ReverseSearchHelp = () => (
  <Paper
    elevation={0}
    sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
  >
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      {t('How reverse search matches', '逆引き検索の仕組み')}
    </Typography>
    <Typography variant="body2" color="text.secondary" paragraph>
      {t(
        'Each address is stored as a row of values — one per level. Reverse search returns a postal code when your query equals a single cell exactly (in any script). It does not match part of a cell, a full address, or several cells joined together.',
        '住所は階層ごとの値（セル）として保存されています。逆引き検索は、入力が1つのセルの値に完全一致したときに、その郵便番号を返します。セルの一部、住所全体、複数セルの連結では一致しません。'
      )}
    </Typography>

    {/* How the data is stored: rows = addresses, cells = level values (JA / EN). */}
    <Box sx={{ overflowX: 'auto', mb: 1 }}>
      <Table size="small" sx={{ minWidth: 760, '& td, & th': { borderColor: 'divider', verticalAlign: 'top' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              {t('Postal code', '郵便番号')}
            </TableCell>
            {LEVEL_HEADERS.map((h) => (
              <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ADDR_ROWS.map((row) => (
            <TableRow key={row.postal}>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.postal}</TableCell>
              {row.cells.map((cell, i) => (
                <TableCell key={i}><DataCell cell={cell} /></TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
      {t(
        'Each row is one stored address (a postal code); each cell is its value for that level. “Exact match” = your query equals the value in one cell. Large buildings get their own codes — a general one plus separate codes per floor, so the floor (10階) is part of the building cell.',
        '各行は1つの保存住所（郵便番号）、各セルはその階層の値です。「完全一致」とは、入力がいずれか1つのセルの値と等しいことを指します。大きな建物には独自の番号（建物全体用＋階ごと）が割り当てられ、階数（10階）も建物セルの一部です。'
      )}
    </Typography>

    {/* Concrete normalization example. */}
    <Typography variant="body2" fontWeight="bold" gutterBottom>
      {t('Before matching, the query is normalized', '照合前に入力は正規化されます')}
    </Typography>
    <Box
      sx={{
        p: 1.5, mb: 1, bgcolor: 'background.paper', borderRadius: 1,
        border: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
      }}
    >
      <Typography component="span" sx={{ fontWeight: 600 }}>新宿アイランドタワー10階</Typography>
      <ArrowRightAlt fontSize="small" sx={{ color: 'text.disabled' }} />
      <Typography component="span" sx={{ fontWeight: 600, color: 'secondary.main' }}>新宿アイランドタワ10階</Typography>
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
      {t(
        'Lowercase; spaces, hyphens and long-vowel marks removed. The mark ー is dropped (タワー → タワ), so “Shinjuku I-Land Tower 10F” → “shinjukuilandtower10f”. Typing the name with or without ー matches either way — the stored cell is normalized the same.',
        '小文字化し、スペース・ハイフン・長音記号を除去します。「ー」は削除され（タワー → タワ）、「Shinjuku I-Land Tower 10F」→「shinjukuilandtower10f」となります。「ー」の有無どちらで入力しても一致します（保存側のセルも同様に正規化されます）。'
      )}
    </Typography>

    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2" color="success.main" fontWeight="bold" gutterBottom>
        {t('Matches', '一致する例')}
      </Typography>
      {MATCHES.map(([q, reason]) => (
        <ExampleRow key={q + reason} ok query={q} reason={reason} />
      ))}
    </Box>

    <Box>
      <Typography variant="body2" color="error.main" fontWeight="bold" gutterBottom>
        {t("Won't match", '一致しない例')}
      </Typography>
      {FAILS.map(([q, reason]) => (
        <ExampleRow key={q + reason} ok={false} query={q} reason={reason} />
      ))}
    </Box>
  </Paper>
);

export default ReverseSearchHelp;
